import * as React from "react";
import { useMemo, useState } from "react";
import { Button } from "primereact/button";
import { changeSidebarPane, updateCronConfig } from "../../../editorSlice";
import { useAppDispatch, useAppSelector } from "../../../../../hooks/hooks";
import classes from "./CrontabView.module.scss";
import { useTranslation } from "react-i18next";
import { Dropdown } from "primereact/dropdown";
import CronPlanningPreview from "./planning/CronPlanningPreview";
import { HourlyForm } from "./hourly/HourlyForm";
import AnnuallyForm from "./annually/AnnuallyForm";
import MonthlyForm from "./monthly/MonthlyForm";
import WeeklyForm from "./weekly/WeeklyForm";
import DailyForm from "./daily/DailyForm";
import { ConfirmDialog, confirmDialog } from "primereact/confirmdialog";
import { MinutesForm } from "./minutes/MinutesForm";

const MINUTES: string = "minutes";
const HOURLY: string = "hourly";
const DAILY: string = "daily";
const WEEKLY: string = "weekly";
const MONTHLY: string = "monthly";
const ANNUALLY: string = "annually";

export interface Cron {
  minutes: string;
  hours: string;
  dayMonth: string;
  month: string;
  dayWeek: string;
}

export interface Props {
  onCronChanged: (val: Cron) => void;
  date: Date;
  day?: number;
}

export const defaultCronObject: Cron = {
  minutes: "*",
  hours: "*",
  dayMonth: "*",
  month: "*",
  dayWeek: "*",
};
const regexIntervalMinute = /^\*\/([1-9]|[1-5][0-9])$/;
const CRONTAB_MAX_PARAMS = 5;

const stringCronToObjectCron = (cron: string | undefined): Cron | undefined => {
  if (!cron || cron.trim() === "") return undefined;
  const split = cron.split(" ");
  if (split.length !== CRONTAB_MAX_PARAMS) return undefined;
  return {
    minutes: split[0],
    hours: split[1],
    dayMonth: split[2],
    month: split[3],
    dayWeek: split[4],
  };
};

const cronObjectToCronString = (cron: Cron): string => {
  return `${cron.minutes} ${cron.hours} ${cron.dayMonth} ${cron.month} ${cron.dayWeek}`;
};

const getDateFromCrontab = (cron: Cron) => {
  const date = new Date();
  if (cron?.hours && !Number.isNaN(+cron.hours)) {
    date.setHours(+cron.hours);
  }
  if (
    (cron?.minutes !== undefined && cron?.minutes !== null   && !Number.isNaN(+cron.minutes)) ||
    regexIntervalMinute.test(cron.minutes)
  ) {
    if (regexIntervalMinute.test(cron.minutes)) {
      const splitCronIntervalMinute = cron.minutes.split("/");
      date.setMinutes(+splitCronIntervalMinute[1]);
    } else {
      date.setMinutes(+cron.minutes);
    }
  }
  if (cron?.dayMonth && !Number.isNaN(+cron.dayMonth)) {
    date.setDate(+cron.dayMonth);
  }
  if (cron?.month && !Number.isNaN(+cron.month)) {
    //date.getMonth() in js : [0-11] but cron months are [1-12]
    //So we have to decrease by 1 the cron.month property to get the right month
    date.setMonth(+cron.month - 1);
  }
  return date;
};

const CrontabView = () => {
  const topologySlice = useAppSelector((s) => s.editor.current);
  const dispatch = useAppDispatch();

  const { t: translate } = useTranslation();

  const RECURRENCES: { label: string; value: string }[] = useMemo(() => {
    return [
      { label: translate("cron-tab.input.recurrence.minutes"), value: MINUTES },
      { label: translate("cron-tab.input.recurrence.hourly"), value: HOURLY },
      { label: translate("cron-tab.input.recurrence.daily"), value: DAILY },
      { label: translate("cron-tab.input.recurrence.weekly"), value: WEEKLY },
      { label: translate("cron-tab.input.recurrence.monthly"), value: MONTHLY },
      {
        label: translate("cron-tab.input.recurrence.annually"),
        value: ANNUALLY,
      },
    ];
  }, []);

  //fetch existing data or init with default data
  let cronObject = useMemo(() => {
    return (
      stringCronToObjectCron(topologySlice?.schedule) || {
        ...defaultCronObject,
        minutes: new Date().getMinutes().toString(),
      }
    );
  }, [topologySlice?.schedule]);

  const [crontab, setCrontab] = useState(cronObject);

  const date = getDateFromCrontab(crontab);

  const day = isNaN(+crontab.dayWeek) ? date.getDay() : +crontab.dayWeek;

  const recurrenceChoice = useMemo(() => {
    if (!topologySlice?.schedule) return RECURRENCES[0];
    let stringRec: string;
    if (cronObject.month !== "*" && cronObject.dayMonth !== "*") {
      stringRec = ANNUALLY;
    } else if (cronObject.dayMonth !== "*") {
      stringRec = MONTHLY;
    } else if (cronObject.dayWeek !== "*") {
      stringRec = WEEKLY;
    } else if (cronObject.hours !== "*") {
      stringRec = DAILY;
    } else if (regexIntervalMinute.test(cronObject.minutes)) {
      stringRec = MINUTES;
    } else {
      stringRec = HOURLY;
    }
    return RECURRENCES.find((rec) => rec.value === stringRec) ?? RECURRENCES[0];
  }, [topologySlice?.schedule, cronObject]);

  const [recurrence, setRecurrence] = useState(recurrenceChoice.value);

  const updateCrontabHandler = (value: Cron) => {
    console.log("cron", value);
    setCrontab({
      ...defaultCronObject,
      ...value,
    });
  };

  const reactiveForm = useMemo(() => {
    switch (recurrence) {
      case MINUTES:
        return <MinutesForm onCronChanged={updateCrontabHandler} date={date} />;
      case HOURLY:
        return <HourlyForm onCronChanged={updateCrontabHandler} date={date} />;
      case DAILY:
        return <DailyForm onCronChanged={updateCrontabHandler} date={date} />;
      case WEEKLY:
        return (
          <WeeklyForm
            onCronChanged={updateCrontabHandler}
            date={date}
            day={day}
          />
        );
      case MONTHLY:
        return <MonthlyForm onCronChanged={updateCrontabHandler} date={date} />;
      case ANNUALLY:
        return (
          <AnnuallyForm onCronChanged={updateCrontabHandler} date={date} />
        );
      default:
        return <HourlyForm onCronChanged={updateCrontabHandler} date={date} />;
    }
  }, [recurrence, date, day, updateCrontabHandler]);

  const onRecurrenceChangedHandler = (recurrence: string) => {
    setRecurrence(recurrence);
  };

  const accept = () => {
    dispatch(updateCronConfig({ schedule: "" }));
    dispatch(changeSidebarPane({ pane: "settings" }));
  };

  const reject = () => {};

  const saveCronConfig = () => {
    dispatch(updateCronConfig({ schedule: cronObjectToCronString(crontab) }));
    dispatch(changeSidebarPane({ pane: "settings" }));
  };

  const cronString = cronObjectToCronString(crontab);
  const dialogMessage = useMemo(
    () => <div className="flex flex-column align-items-center">
      <span>{translate("cron-tab.input.delete_cron_message")}</span>
      <span className={classes.info}>{translate("cron-tab.cron_deletion")}</span>
    </div>,
    []
  );
  const dialogHeader = useMemo(
    () => translate("cron-tab.input.delete_cron_header"),
    []
  );

  const removeCronHandler = () => {
    const acceptLabel = translate("yes");
    const rejectLabel = translate("no");

    confirmDialog({
      message: dialogMessage,
      header: dialogHeader,
      acceptClassName: "p-button-danger",
      acceptLabel,
      rejectLabel,
      accept,
      reject,
    });
  };

  return (
    <>
      <ConfirmDialog />
      <section className={classes["cron-panel__back-button"]}>
        <Button
          className="p-button-text mt-2"
          type="button"
          label={translate("cron-tab.title")}
          onClick={() => {
            dispatch(changeSidebarPane({ pane: "settings" }));
          }}
          icon="pi pi-arrow-left"
        />
      </section>
      <section className={classes["cron-panel__component"]}>
        <section className={classes["cron-panel__cron-config"]}>
          <div className={classes["cron-panel__form-control"]}>
            <label>{translate("cron-tab.recurrence")}</label>
            <Dropdown
              scrollHeight="250px"
              value={recurrence}
              options={RECURRENCES}
              onChange={(e) => onRecurrenceChangedHandler(e.value)}
              placeholder={translate("cron-tab.recurrence")}
            />
          </div>
          <div className={classes["cron-panel__cron-form"]}>{reactiveForm}</div>
        </section>
        <CronPlanningPreview cron={cronString} />
      </section>
      <section className={classes["cron-panel__actions"]}>
        <Button
          type="button"
          className="p-button-danger"
          onClick={removeCronHandler}
          label={translate("cron-tab.remove")}
          disabled={!topologySlice?.schedule}
        />
        <Button
          type="button"
          label={translate("cron-tab.save")}
          onClick={saveCronConfig}
        />
      </section>
    </>
  );
};

export default CrontabView;
